import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Edit, Building2, Package } from 'lucide-react';
import { fetchSupplierById } from '../services/api';

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSupplier();
  }, [id]);

  const loadSupplier = async () => {
    try {
      const data = await fetchSupplierById(id);
      setSupplier(data);
    } catch (error) {
      console.error('Failed to load supplier:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading supplier details...</div>;
  }

  if (!supplier) {
    return <div className="text-center py-12">Supplier not found</div>;
  }

  // Parse address if it's a string
  let address = supplier.address;
  if (typeof address === 'string') {
    try {
      address = JSON.parse(address);
    } catch (e) {
      address = {};
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/suppliers')} className="btn-secondary">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-3xl font-bold">Supplier Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Supplier Info */}
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center">
                <Building2 className="text-primary-600" size={32} />
              </div>
              <button
                onClick={() => navigate(`/suppliers/${id}/edit`)}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit size={16} />
                Edit
              </button>
            </div>

            <h2 className="text-xl font-semibold mb-4">{supplier.name}</h2>

            <div className="space-y-3">
              {supplier.category && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Package size={18} className="text-gray-400" />
                  <span className="text-sm font-medium">{supplier.category}</span>
                </div>
              )}
              {supplier.contact_person && (
                <div className="text-gray-700">
                  <span className="text-sm font-medium">Contact: </span>
                  <span className="text-sm">{supplier.contact_person}</span>
                </div>
              )}
              {supplier.email && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Mail size={18} className="text-gray-400" />
                  <span className="text-sm">{supplier.email}</span>
                </div>
              )}
              {supplier.phone && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Phone size={18} className="text-gray-400" />
                  <span className="text-sm">{supplier.phone}</span>
                </div>
              )}
              {address && (address.street || address.city) && (
                <div className="flex items-start gap-3 text-gray-700">
                  <MapPin size={18} className="text-gray-400 mt-1" />
                  <div className="text-sm">
                    {address.street && <p>{address.street}</p>}
                    {(address.city || address.state) && (
                      <p>{address.city}{address.city && address.state && ', '}{address.state}</p>
                    )}
                    {address.zip_code && <p>{address.zip_code}</p>}
                    {address.country && <p>{address.country}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold mb-4">Supplier Terms</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Lead Time:</span>
                <span className="font-semibold">{supplier.lead_time_days || 0} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Min Order Value:</span>
                <span className="font-semibold">${Number(supplier.min_order_value || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Member Since:</span>
                <span className="text-sm">{new Date(supplier.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info Section (can be expanded later) */}
        <div className="lg:col-span-2">
          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Purchase History</h3>
            <p className="text-gray-500 text-center py-8">
              Purchase order tracking coming soon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDetail;
